import { i18n, t } from "i18next"
import { stringify } from "query-string"

import {
  IntegrationProvider,
  SubscriptionPlanResponse,
  UserSubscription,
} from "@treefort/api-spec"
import {
  FreeTrialEligibility,
  FreeTrialEligibilityList,
} from "@treefort/api-spec"
import filter from "@treefort/lib/filter"

import config from "../../config"
import api from "../api"
import {
  getAppStorePrice,
  getBestPlayStorePrice,
  getBestPlayStoreOfferToken,
  getSubscriptionProducts,
} from "../in-app-purchases"
import { logError } from "../logging"
import openUrl from "../open-url"
import { openStripeBillingPortal } from "../stripe"
import { openWebPaymentBillingPortal } from "../web-payment"
import { getPaymentProvidersForPlatform } from "./providers"

export {
  canCancelPlanFromProvider,
  canPurchasePlanFromProvider,
  canOpenBillingPortalForProvider,
  getPaymentProvidersForPlatform,
} from "./providers"

export type AvailableSubscriptionPlan = SubscriptionPlanResponse & {
  archivedAt?: undefined
  // Necessary to make purchases on Andriod, available via react-native-iap
  offerToken?: string
}

export async function getAvailableSubscriptionPlans(
  allSubscriptionPlans: SubscriptionPlanResponse[],
): Promise<AvailableSubscriptionPlan[]> {
  // Bail early if we have no subscription plans
  if (!allSubscriptionPlans.length) {
    return []
  }

  try {
    // Fetch all in-app-purchase products up front
    const products = await getSubscriptionProducts(allSubscriptionPlans)

    const providers = await getPaymentProvidersForPlatform()

    // Filter out unavailable subscription plans and add additional details to
    // the remaining ones.
    const availableSubscriptionPlans = allSubscriptionPlans.flatMap((plan) => {
      if (plan.archivedAt || !providers.includes(plan.provider)) {
        return []
      }

      switch (plan.provider) {
        case "playStore": {
          const offerToken = getBestPlayStoreOfferToken(plan, products)
          const price = getBestPlayStorePrice(plan, products)
          return offerToken && price
            ? {
                ...plan,
                archivedAt: undefined,
                provider: "playStore" as const,
                offerToken,
                price,
              }
            : []
        }
        case "appStore": {
          const price = getAppStorePrice(plan, products)
          return price
            ? {
                ...plan,
                archivedAt: undefined,
                provider: "appStore" as const,
                price,
              }
            : []
        }
        default:
          return {
            ...plan,
            archivedAt: undefined,
          }
      }
    })

    // Log an error if plans exist but we can't find any to display
    const missingSubscriptionPlans = !availableSubscriptionPlans.length
      ? allSubscriptionPlans.filter(
          (plan) => !plan.archivedAt && providers.includes(plan.provider),
        )
      : []
    if (missingSubscriptionPlans.length) {
      // NOTE: The Sentry alert rule has to be kept in sync with this message
      logError(new Error(`[Subscription Plans] Missing products`), {
        extra: {
          subscriptionPlanIds: missingSubscriptionPlans.map((plan) => plan.id),
        },
      })
    }

    // If we have preferred plans to display then ONLY display those plans (i.e.
    // don't display non-preferred plans next to preferred plans).
    if (availableSubscriptionPlans.some(isPreferredSubscriptionPlan)) {
      return availableSubscriptionPlans.filter(isPreferredSubscriptionPlan)
    }

    return availableSubscriptionPlans
  } catch (cause) {
    throw new Error(
      "[Subscription Plans] Failed to get available subscription plans",
      { cause },
    )
  }
}
/**
 * NOTE: This will not include group membership subscription plans since users
 * don't have permission to fetch all of those at once
 */
export async function getSubscriptionPlans(options?: {
  offeringIds?: number[]
  providers?: IntegrationProvider[]
  appPreviewToken?: string
}) {
  const result = await api.get<
    Exclude<SubscriptionPlanResponse, { provider: "groupMembership" }>[]
  >("/subscription/plans", {
    params: {
      offeringId: options?.offeringIds,
      provider: options?.providers?.filter(
        (provider) => provider !== "groupMembership",
      ),
    },
    headers: filter(
      { "X-Treefort-App-Preview-Token": options?.appPreviewToken },
      Boolean,
    ),
  })
  return result.data
}

export const getSubscriptionPlan = async (
  id: number,
): Promise<SubscriptionPlanResponse> => {
  const result = await api.get<SubscriptionPlanResponse>(
    `/subscription/plans/${id}`,
  )
  return result.data
}

export function formatPlanIntervalShort(
  plan: Pick<
    Exclude<SubscriptionPlanResponse, { provider: "groupMembership" }>,
    "interval" | "intervalCount"
  >,
) {
  switch (plan.interval) {
    case "month":
      return t("/{{number}} months", { number: plan.intervalCount })
    case "year":
      return t("/{{number}} years", { number: plan.intervalCount })
  }
}

export function formatPlanIntervalLong(
  plan: Pick<
    Exclude<SubscriptionPlanResponse, { provider: "groupMembership" }>,
    "interval" | "intervalCount"
  >,
) {
  switch (plan.interval) {
    case "month":
      return t("Billed every {{number}} months", {
        number: plan.intervalCount,
      })
    case "year":
      return t("Billed every {{number}} years", {
        number: plan.intervalCount,
      })
  }
}

export const getFreeTrialEligibilities = async (
  subscriptionPlanIds: number[],
): Promise<FreeTrialEligibility[]> => {
  if (!subscriptionPlanIds.length) {
    return []
  }
  const result = await api.get<FreeTrialEligibilityList>(
    `/subscription/free-trial-eligibility`,
    { params: { planId: subscriptionPlanIds } },
  )
  return result.data.eligibilities
}

export async function openBillingPortal({
  subscription,
  subscriptionPlan,
  i18n,
}: {
  subscription: UserSubscription | undefined
  subscriptionPlan: SubscriptionPlanResponse
  i18n: i18n
}): Promise<void> {
  switch (subscriptionPlan.provider) {
    case "playStore": {
      // Google allows us to link directly to a specific subscription if it is
      // active. If the subscription is not active or the user doesn't have one
      // then we need to omit these query parameters. This will open the
      // top-level subscription page instead of triggering a potential 404.
      const queryString =
        subscription && !subscription.deactivatedAt
          ? `?${stringify({ package: config.ANDROID_APP_ID, sku: subscriptionPlan.productId })}`
          : ""
      return openUrl(
        `https://play.google.com/store/account/subscriptions${queryString}`,
        { openInApp: false, parentalGateway: false },
      )
    }
    case "appStore": {
      return openUrl("https://apps.apple.com/account/subscriptions", {
        openInApp: false,
        parentalGateway: false,
      })
    }
    case "stripe":
      return openStripeBillingPortal({ i18n })
    case "webPayment":
      return openWebPaymentBillingPortal({ i18n })
  }
}

/**
 * This is used to prefer plans from web-based providers like Stripe and custom
 * Web Payment integrations over plans from Apple/Google if both are available.
 * Why prefer web-based plans? They're better for users (easier to manage and
 * cancel) and better for tenants (easier to refund, provide customer service).
 * When might both types of plans be available? When payment freedom is granted
 * (see `isPaymentFreedomGranted`).
 */
function isPreferredSubscriptionPlan(plan: SubscriptionPlanResponse) {
  return plan.provider !== "appStore" && plan.provider !== "playStore"
}
